/* ═══════════════════════════════════════════════════════
   NYC Suite — Calendar Event Builders (pure functions)
   ═══════════════════════════════════════════════════════
   Shapes a session into a Google Calendar event payload.
   No network, no side effects. Consumed by calendarSync.js.
*/
import { SESSION_TYPE_LABELS, SESSION_TYPES } from "./constants.js";
import { fmtTime } from "./utils.js";

// Sentinel keys for sendUpdatesFor — used instead of magic strings at call sites.
export const EVENT_CREATED = "__created__";
export const EVENT_DELETED = "__deleted__";

// "HH:mm", 24-hour, zero-padded. The same shape the card twins accept for a
// time; kept local so this module never depends on crewCardRules.
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
export const isHHmm = (t) => typeof t === "string" && HHMM_RE.test(t);

// The crew call that moves the event (SHEETS-V2-SLICE-2-SPEC 4.1, owner's
// answer 1 of 2026-09-22): a card `crewCall` starts the crew calendar event
// when the session is timed (call AND wrap), the value is a valid HH:mm
// strictly before the wrap time, and it differs from the session call
// (a crew call equal to the talent call changes nothing). "" otherwise, so
// the event keeps the session call, an all-day session stays all-day, and a
// crew call at or after the wrap is ignored.
export function eventCrewCall(session, crewCall = "") {
  if (!session?.callTime || !isHHmm(session?.wrapTime)) return "";
  if (!isHHmm(crewCall) || crewCall >= session.wrapTime) return "";
  return crewCall === session.callTime ? "" : crewCall;
}

// Google Calendar event colorIds (1-11). Closest named colors to the in-app palette.
// Keys pulled from SESSION_TYPES enum so a rename there propagates here.
export const SESSION_TYPE_COLOR_ID = {
  [SESSION_TYPES[0]]: "7",   // flight  — Peacock (teal)
  [SESSION_TYPES[1]]: "3",   // setup   — Grape (purple)
  [SESSION_TYPES[2]]: "9",   // capture — Blueberry (blue)
  [SESSION_TYPES[3]]: "5",   // strike  — Banana (amber)
};

// ✈️ flight · 🏢 studio · 🚛 on-location with Big Rig/Head Rig · 🚚 on-location without
export function weightEmoji(session, job) {
  if (session?.sessionType === "flight") return "✈️";
  if (session?.isStudio) return "🏢";
  const equip = Array.isArray(job?.selectedEquipment) ? job.selectedEquipment : [];
  const hasHeavyRig = equip.includes("Big Rig") || equip.includes("Head Rig");
  return hasHeavyRig ? "🚛" : "🚚";
}

export function buildEventTitle(job, session) {
  const client = job?.clientName || "Client TBD";
  const subject = job?.description || "Untitled";
  const typeLabel = SESSION_TYPE_LABELS[session?.sessionType] || "Session";
  return `${weightEmoji(session, job)} ${client} — ${subject} (${typeLabel})`;
}

function buildEventLocation(session, job) {
  const parts = [
    session?.locationVenue || job?.locationVenue,
    session?.locationAddress || job?.locationAddress,
    session?.locationCity || job?.locationCity,
    session?.locationState || job?.locationState,
    session?.locationZip || job?.locationZip,
  ];
  return parts.filter(Boolean).join(", ") || session?.location || job?.location || "";
}

// The words before the session call on the line under a moved start: what
// happens at that time. "Talent arrives" on a people job or one whose subject
// is unknown (the legacy default); "Start" for objects and places.
// crewSubject is the crew card's subject as DataContext validates it:
// "people" | "objects" | "places" | "".
export const startLineLabel = (crewSubject = "") =>
  (crewSubject === "objects" || crewSubject === "places" ? "Start" : "Talent arrives");

export function buildEventDescription(job, session, { contactName, contactPhone, jobUrl, crewCardUrl, crewCall, crewSubject } = {}) {
  const lines = [];
  // The event now starts at the crew call; what happens at the session call
  // (the talent arrives, or the work starts) is the first thing the crew
  // reads. Only when the two differ (eventCrewCall is "" when the crew call
  // equals the session call).
  if (eventCrewCall(session, crewCall)) lines.push(`${startLineLabel(crewSubject)} ${fmtTime(session.callTime)}`);
  if (job?.description) lines.push(`Subject: ${job.description}`);
  if (Array.isArray(job?.selectedEquipment) && job.selectedEquipment.length) {
    lines.push(`Equipment: ${job.selectedEquipment.join(", ")}`);
  }
  const crew = Array.isArray(job?.crew)
    ? job.crew
    : (typeof job?.crew === "string" ? job.crew.split(",").map(s => s.trim()).filter(Boolean) : []);
  if (crew.length) lines.push(`Crew: ${crew.join(", ")}`);
  if (contactName || contactPhone) {
    lines.push(`Client contact: ${[contactName, contactPhone].filter(Boolean).join(" · ")}`);
  }
  if (jobUrl) lines.push("", `Open in Suite: ${jobUrl}`);
  // Deployed call sheet link (docs/crew-cards/DESIGN.md). The server writes the
  // same line on deploy; carrying it here means a later client-side sync
  // rebuilds the description WITH the line instead of erasing it.
  if (crewCardUrl) lines.push(`Call sheet: ${crewCardUrl}`);
  return lines.join("\n");
}

// Returns null when the session has no date (cannot place on calendar yet).
// opts.crewCall: the card's crew call ("HH:mm" or ""); see eventCrewCall for
// when it moves the start. opts.crewSubject: the crew card's subject, for
// the wording of the line under a moved start (startLineLabel). Everything
// else about the event is unchanged.
export function sessionToEvent(job, session, opts = {}) {
  if (!session?.date) return null;
  const { contactName, contactPhone, jobUrl, crewCardUrl = "", crewCall = "", crewSubject = "", attendeeEmails = [], timeZone = "America/New_York" } = opts;

  const attendees = attendeeEmails.filter(Boolean).map(email => ({ email }));
  const event = {
    summary: buildEventTitle(job, session),
    location: buildEventLocation(session, job),
    description: buildEventDescription(job, session, { contactName, contactPhone, jobUrl, crewCardUrl, crewCall, crewSubject }),
    colorId: SESSION_TYPE_COLOR_ID[session.sessionType],
  };
  // Omit attendees when empty — including [] forces "with guests" semantics and triggers false diffs on PATCH.
  if (attendees.length) event.attendees = attendees;

  if (session.callTime && session.wrapTime) {
    const start = eventCrewCall(session, crewCall) || session.callTime;
    event.start = { dateTime: `${session.date}T${start}:00`, timeZone };
    event.end = { dateTime: `${session.date}T${session.wrapTime}:00`, timeZone };
  } else {
    event.start = { date: session.date };
    // Google all-day end is exclusive — add one day.
    const d = new Date(session.date + "T00:00:00");
    d.setDate(d.getDate() + 1);
    event.end = { date: d.toISOString().slice(0, 10) };
  }

  return event;
}

// Maps the set of fields that changed into sendUpdates strategy per spec:
//   "all" for new, time/date, or delete. "none" for silent edits (description/location/equipment).
//   A crew call change moves the start the crew is invited to, so it counts as a time change.
export function sendUpdatesFor(changedFields = []) {
  if (changedFields.includes(EVENT_CREATED) || changedFields.includes(EVENT_DELETED)) return "all";
  const timeFields = ["date", "callTime", "wrapTime", "crewCall"];
  if (changedFields.some(f => timeFields.includes(f))) return "all";
  return "none";
}
